/**
 * Keeps the approve/reject staging rules for the assets list in one pure module so a
 * background refetch can keep the reviewer's per-card choices instead of re-seeding them.
 */
import type { AssetDto } from 'src/store/generated/leela-api-generated';
import { isAssetFlagged } from 'src/utils/assetEvalBadge';
import { assetStrategyKey } from 'src/utils/assetKeys';

export interface AssetSelection {
  readonly selected: AssetDto[];
  readonly rejected: AssetDto[];
}

export interface AssetSelectionDefaults {
  readonly isPending: boolean;
  readonly selectAll: boolean;
}

export interface KeepAssetChoicesOptions {
  readonly assets: readonly AssetDto[];
  readonly defaultChoice: readonly AssetDto[];
  readonly previousAssetKeys: ReadonlySet<string>;
}

interface PendingSelectAllAssets {
  readonly approved: AssetDto[];
  readonly rejected: AssetDto[];
}

export function partitionPendingSelectAllAssets (
  assets: readonly AssetDto[],
): PendingSelectAllAssets {
  const approved: AssetDto[] = [];
  const rejected: AssetDto[] = [];

  assets.forEach((asset) => {
    (isAssetFlagged(asset.evaluatedRules) ? rejected : approved).push(asset);
  });

  return { approved, rejected };
}

/**
 * Stages the select-all default. Pending assets flagged by evaluation start as rejected;
 * nothing is submitted until the reviewer confirms.
 */
export function getDefaultAssetSelection (
  assets: readonly AssetDto[],
  { isPending, selectAll }: AssetSelectionDefaults,
): AssetSelection {
  if (!selectAll) {
    return { selected: [], rejected: isPending ? [...assets] : [] };
  }
  if (!isPending) {
    return { selected: [...assets], rejected: [] };
  }
  const { approved, rejected } = partitionPendingSelectAllAssets(assets);
  return { selected: approved, rejected };
}

/**
 * Rebuilds one choice list (selected or rejected) for a new asset list: assets seen before keep
 * their membership in `previousChoice`, new assets follow `defaultChoice`, removed assets drop out.
 */
export function keepAssetChoices (
  previousChoice: readonly AssetDto[],
  { assets, defaultChoice, previousAssetKeys }: KeepAssetChoicesOptions,
): AssetDto[] {
  const previousChoiceKeys = new Set(previousChoice.map(assetStrategyKey));
  const defaultChoiceKeys = new Set(defaultChoice.map(assetStrategyKey));

  return assets.filter((asset) => {
    const key = assetStrategyKey(asset);
    return previousAssetKeys.has(key) ? previousChoiceKeys.has(key) : defaultChoiceKeys.has(key);
  });
}

export function hasSameAssetKeys (
  previousAssetKeys: ReadonlySet<string>,
  assetKeys: ReadonlySet<string>,
): boolean {
  return previousAssetKeys.size === assetKeys.size &&
    Array.from(assetKeys).every((key) => previousAssetKeys.has(key));
}
